// ============================================================================
// The session driver: advances a life to a target in-game age through the
// engine's canonical input path, and rebuilds identically from the action log.
//
// ONE code path for everything (migration contract #3): at each year boundary
// the filed plan is applied via engine.applyPlan (auto-filing "continue last
// plan" when the player hasn't submitted one — GDD §3.2), then time advances
// one whole 1/12-year substep at a time via engine.doSubstep — the exact same
// substep sequence engine.stepYear/settleTo produce. Because settlement is
// chunk-invariant (G7), driving in UI-tick-sized nibbles, in one catch-up leap
// after a closed tab, or in a from-scratch rebuild from the log all yield
// BIT-IDENTICAL state. apps/web/test/driver.test.ts proves it against
// engine.simulateLife.
//
// The driver is pure and DOM-free: no clock reads, no storage. It is the
// module a v2 PlayerDO would run unchanged.
// ============================================================================

import {
  applyAdjustment,
  applyPlan,
  canEnroll,
  counterfactual,
  estateRecordOf,
  healthRates,
  capacityMult,
  crossTime,
  currentIncome,
  doSubstep,
  initState,
  isReviewEntry,
  livingGain,
  moraleTargetFor,
  moraleTargetOf,
  newSave,
  normalizePolicy,
  settleTo,
  studySpeed,
  type Mode,
  type Params,
  type PlanAction,
  type SaveFile,
  type State,
} from '@lifespan/engine';

/** The default first plan: Standard work, $0 Living, not studying. */
export const DEV_PLAN: PlanAction = { mode: 'std', livingFrac: 0, study: false };

/** The slice of the plan the player controls. Study is review-only (M5). */
export interface PlanDraft {
  mode: Mode;
  livingFrac: number;
  /** study intent — consumed only when the submission is an annual review */
  study?: boolean;
}

/** One sample per settled substep — sparkline/statement data (M: M4, H: M6). */
export interface Sample {
  age: number;
  S: number;
  M: number;
  H: number;
}

/**
 * Accumulator snapshot taken at each year boundary (this-year ledger lines).
 * Captured BEFORE the year's plan is applied, so review-time cash effects
 * (tuition — GDD §15.1-5's cash set piece) fall inside the year they open.
 */
export interface YearStart {
  year: number;
  age: number;
  S: number;
  interestAccum: number;
  earnedAccum: number;
  colAccum: number;
  livingAccum: number;
  tuitionAccum: number;
  cardCashAccum: number;
}

export interface Session {
  save: SaveFile;
  state: State;
  samples: Sample[];
  yearStarts: YearStart[];
  /** count of log entries already applied to `state` (the drain cursor) */
  logIndex: number;
  /** count of annual reviews applied (year N is open iff reviewedYears > N) */
  reviewedYears: number;
}

function yearStartOf(state: State, year: number, P: Params): YearStart {
  return {
    year,
    age: P.startAge + year,
    S: state.S,
    interestAccum: state.interestAccum,
    earnedAccum: state.earnedAccum,
    colAccum: state.colAccum,
    livingAccum: state.livingAccum,
    tuitionAccum: state.tuitionAccum,
    cardCashAccum: state.cardCashAccum,
  };
}

/** A fresh session: empty log, initial state, the birth sample. */
export function newSession(P: Params, createdAtUnix: number): Session {
  const save = newSave(P, createdAtUnix);
  const state = initState(P);
  return {
    save,
    state,
    samples: [{ age: P.startAge, S: state.S, M: state.M, H: state.H }],
    yearStarts: [],
    logIndex: 0,
    reviewedYears: 0,
  };
}

/** The plan that continues if no new one is filed (last logged, else devMask). */
export function lastPlan(save: SaveFile): PlanAction {
  const last = save.log[save.log.length - 1];
  return last ? { ...last.plan } : { ...DEV_PLAN };
}

/** Apply one log entry to the session state (review or adjustment). */
function applyEntry(session: Session, entryIndex: number, P: Params): void {
  const entry = session.save.log[entryIndex]!;
  if (isReviewEntry(entry)) {
    // Baseline BEFORE the review's own cash effects: the This-year window
    // opens at the boundary, so tuition deducted by applyPlan lands inside
    // the year that paid it (not between two year slices).
    session.yearStarts.push(yearStartOf(session.state, session.reviewedYears, P));
    applyPlan(session.state, entry.plan, P);
    // The log carries the ENACTED study state, not the asked-for one: applyPlan
    // may refuse enrolment (gate/broke), and leaving the refused intent logged
    // would arm every future auto-filed continuation to retry — charging
    // tuition years later, with no fresh player action, the moment the gates
    // first pass ("the warning is the fair part" forbids that). Rewriting is
    // replay-safe: a refused study:true and an applied study:false leave
    // bit-identical state, so the rewritten log replays to the same life
    // (asserted in apps/web/test/skills.test.ts).
    entry.plan.study = session.state.studying;
    session.reviewedYears += 1;
  } else {
    applyAdjustment(session.state, entry.plan);
  }
  session.logIndex = entryIndex + 1;
}

/**
 * Advance the session to the given in-game age (whole substeps only — a target
 * inside a substep leaves state exactly at the last completed boundary, same
 * floor rule as engine.settleTo). At each settled age, pending log entries at
 * that age drain in log order (review opens the year, adjustments follow);
 * crossing a year boundary with no filed review auto-files "continue last
 * plan" (GDD §3.2). Idempotent: driving to an age already reached is a no-op.
 *
 * The applyPlan/applyAdjustment/doSubstep sequence this produces is exactly
 * what engine.replay produces from the same log — proven bit-identically in
 * apps/web/test.
 */
export function driveTo(session: Session, targetAge: number, P: Params): void {
  const { state, save } = session;
  const targetN = Math.floor((targetAge - P.startAge) / P.substep + 1e-9);
  const perYear = Math.round(1 / P.substep); // substeps per game year, from the ParamSet
  while (state.alive && state.n < targetN) {
    const ageNow = P.startAge + state.n * P.substep;
    // drain logged entries scheduled up to this settled age, in log order
    while (
      session.logIndex < save.log.length &&
      save.log[session.logIndex]!.atAge <= ageNow + 1e-9
    ) {
      applyEntry(session, session.logIndex, P);
    }
    // a year boundary with no filed review: continue last year's plan
    if (state.n % perYear === 0 && session.reviewedYears === state.n / perYear) {
      save.log.push({ atAge: ageNow, plan: lastPlan(save) });
      applyEntry(session, save.log.length - 1, P);
    }
    doSubstep(state, P);
    session.samples.push({
      age: state.alive ? P.startAge + state.n * P.substep : state.deathAge!,
      S: state.S,
      M: state.M,
      H: state.H,
    });
  }
  // Entries filed AT the final settled age must drain too: in live play the
  // player's submitPlan applied them the instant they were filed, so a rebuild
  // that stops exactly at that age (step-mode file-at-boundary, then reopen)
  // would otherwise leave them pending — stale policy, the already-filed review
  // re-presented as waiting, and a duplicate review corrupting the log on
  // re-file. No auto-file here: a boundary the target lands on still
  // opens-and-waits (auto-filing happens only when time advances past it).
  const settled = state.alive ? P.startAge + state.n * P.substep : state.deathAge!;
  while (
    session.logIndex < save.log.length &&
    save.log[session.logIndex]!.atAge <= settled + 1e-9
  ) {
    applyEntry(session, session.logIndex, P);
  }
}

/**
 * File the player's plan (GDD §3.2 + §4.2). Two cases, decided by where the
 * session sits when the plan is submitted:
 *
 * - Sitting exactly at a year boundary whose review has not yet been filed:
 *   the submission IS that year's annual review. It applies immediately (the
 *   review "opens and waits" — this is the year's start instant; no substeps
 *   have run, so applying now is bit-identical to applying on advance).
 * - Anywhere else: a mid-year adjustment — policy only, effective now.
 *
 * Either way the entry is appended to the log at the current settled age, so
 * replay reproduces the live sequence exactly.
 *
 * Study (M5): the study INTENT is a review-time decision (GDD §3.2 —
 * enrolment happens at the annual review). A review submission carries
 * draft.study through applyPlan (which enrols/resumes/pauses, charging
 * tuition through canEnroll's gates). A mid-year adjustment NEVER touches
 * study — the logged plan echoes the STANDING logged intent (lastPlan), not
 * the transient st.studying, so continuation plans carry the intent forward
 * faithfully even inside the window between a mid-year skill completion
 * (which clears st.studying) and the next boundary: whether the climb
 * continues must never depend on whether the player happened to touch the
 * Living dial mid-year.
 */
export function submitPlan(
  session: Session,
  draft: PlanDraft,
  P: Params,
): 'review' | 'adjust' | null {
  const { state, save } = session;
  if (!state.alive) return null;
  const perYear = Math.round(1 / P.substep);
  const ageNow = P.startAge + state.n * P.substep;
  const atUnreviewedBoundary =
    state.n % perYear === 0 && session.reviewedYears === state.n / perYear;
  if (atUnreviewedBoundary) {
    const plan: PlanAction = {
      mode: draft.mode,
      livingFrac: draft.livingFrac,
      study: !!draft.study,
    };
    save.log.push({ atAge: ageNow, plan });
    applyEntry(session, save.log.length - 1, P);
    return 'review';
  }
  const plan: PlanAction = {
    mode: draft.mode,
    livingFrac: draft.livingFrac,
    study: continuationStudy(session),
  };
  save.log.push({ atAge: ageNow, plan, kind: 'adjust' });
  applyEntry(session, save.log.length - 1, P);
  return 'adjust';
}

/** The plan currently in effect (the engine's live policy). */
export function effectivePlan(session: Session): PlanDraft {
  return { mode: session.state.policy.mode, livingFrac: session.state.policy.livingFrac };
}

/**
 * The STANDING study intent — what stepping past the next unreviewed boundary
 * will actually apply (driveTo's auto-file re-files lastPlan). This is the
 * logged intent, not the transient st.studying: after a mid-year skill
 * completion clears st.studying, the standing intent stays true and the
 * continuation review re-enrols the next skill (charging its tuition). The UI
 * must describe THAT, not the transient (PlanPanel's boundary draft/preview).
 * The log carries enacted study states (see applyEntry), so a refused
 * enrolment never lingers here as a phantom intent.
 */
export function continuationStudy(session: Session): boolean {
  return session.save.log.length > 0
    ? !!lastPlan(session.save).study
    : session.state.studying;
}

/** What the one-year preview reports (the M3 stub of UX-3's diff panel). */
export interface YearPreview {
  /** cash at the next year boundary under the draft plan */
  endS: number;
  /** interest accrued between now and that boundary */
  interest: number;
  /** net cash flow over the previewed stretch (excludes interest) */
  netFlow: number;
  /** income earned over the previewed stretch */
  earned: number;
  /** cost of living accrued over the previewed stretch (§5's survival floor) */
  costOfLiving: number;
  /** Living actually spent over the previewed stretch ($0 while Broke-locked) */
  living: number;
  /**
   * Tuition charged inside the previewed stretch (applyPlan charges it at
   * filing when the review enrols). Without this line the §5 decomposition
   * visibly fails to reconcile in every enrolment year — the exact cash set
   * piece (GDD §2, UX-6) the preview exists to telegraph.
   */
  tuition: number;
  /**
   * First settled age at which the −$25k Broke lock engages inside the
   * previewed stretch, or null (never engages, or was already engaged). The
   * pre-commit telegraph of the cliff — "the warning is the fair part".
   */
  brokeAge: number | null;
  /** the previewed stretch ends in death */
  dies: boolean;
  /** in-game years previewed (partial when submitted mid-year) */
  span: number;
  /** morale equilibrium M* of the draft plan (the ghost the dial moves) */
  draftMstar: number;
  /** morale at the end of the previewed stretch */
  endM: number;
}

/**
 * Exact preview: clone the state, apply the draft exactly the way submitPlan
 * would (the annual review via applyPlan at an unreviewed year boundary, a
 * policy-only adjustment anywhere else — same classification, so review side
 * effects like the J = K promotion are previewed too), and settle to the next
 * year boundary through the real engine. Deterministic, so this is not an
 * estimate — it is the number (cards are off before M7; the projection
 * previews only what the engine will actually do under the draft).
 */
export function previewYear(session: Session, draft: PlanDraft, P: Params): YearPreview {
  const st = structuredClone(session.state);
  if (!st.alive) {
    return {
      endS: st.S,
      interest: 0,
      netFlow: 0,
      earned: 0,
      costOfLiving: 0,
      living: 0,
      tuition: 0,
      brokeAge: null,
      dies: true,
      span: 0,
      draftMstar: st.M,
      endM: st.M,
    };
  }
  const perYear = Math.round(1 / P.substep);
  const startN = st.n;
  const startS = st.S;
  const startInterest = st.interestAccum;
  const startEarned = st.earnedAccum;
  const startCol = st.colAccum;
  const startLiving = st.livingAccum;
  const startTuition = st.tuitionAccum;
  const boundaryN = (Math.floor(st.n / perYear) + 1) * perYear;
  // mirror submitPlan's classification bit-for-bit (identical today because
  // study is pinned false and K never advances before M5, but load-bearing
  // the moment reviews carry promotion/enrolment effects)
  const atUnreviewedBoundary =
    st.n % perYear === 0 && session.reviewedYears === st.n / perYear;
  if (atUnreviewedBoundary) {
    applyPlan(st, { mode: draft.mode, livingFrac: draft.livingFrac, study: !!draft.study }, P);
  } else {
    applyAdjustment(st, { mode: draft.mode, livingFrac: draft.livingFrac });
  }
  // settle one substep at a time — bit-identical to settleTo (G7: chunk-pattern
  // invariant) — so the first Broke-lock engagement is observable at its age
  const wasLocked = st.brokeLock;
  let brokeAge: number | null = null;
  while (st.alive && st.n < boundaryN) {
    doSubstep(st, P);
    if (brokeAge === null && !wasLocked && st.brokeLock) {
      brokeAge = st.alive ? P.startAge + st.n * P.substep : st.deathAge!;
    }
  }
  const interest = st.interestAccum - startInterest;
  const startAge = P.startAge + startN * P.substep;
  return {
    endS: st.S,
    interest,
    netFlow: st.S - startS - interest,
    earned: st.earnedAccum - startEarned,
    costOfLiving: st.colAccum - startCol,
    living: st.livingAccum - startLiving,
    tuition: st.tuitionAccum - startTuition,
    brokeAge,
    dies: !st.alive,
    span: st.alive ? (boundaryN - startN) * P.substep : st.deathAge! - startAge,
    // M* the way the engine will actually integrate it (broke lock zeroes the
    // spend term — moraleTargetFor reads the clone's live policy + lock)
    draftMstar: moraleTargetFor(st, P),
    endM: st.M,
  };
}

/**
 * Rebuild a session from a save's action log (load / import). Same driveTo
 * path as live play, so the rebuilt state, samples, and year ledger are
 * bit-identical to the session that recorded the log.
 */
export function rebuild(save: SaveFile, targetAge: number, P: Params): Session {
  const state = initState(P);
  const session: Session = {
    save,
    state,
    samples: [{ age: P.startAge, S: state.S, M: state.M, H: state.H }],
    yearStarts: [],
    logIndex: 0,
    reviewedYears: 0,
  };
  driveTo(session, targetAge, P);
  return session;
}

/** The settled in-game age of the session (death age once dead). */
export function settledAge(session: Session, P: Params): number {
  const st = session.state;
  return st.alive ? P.startAge + st.n * P.substep : st.deathAge!;
}

// ---------------------------------------------------------------------------
// Derived read models (every number the dashboard shows comes from these —
// UX-6: nothing on screen the ledger cannot reproduce).
// ---------------------------------------------------------------------------

export interface LedgerLines {
  earned: number;
  interest: number;
  costOfLiving: number;
  living: number;
  tuition: number;
  cards: number;
}

/** Lifetime ledger, straight from the engine's accounting accumulators. */
export function lifetimeLedger(state: State): LedgerLines {
  return {
    earned: state.earnedAccum,
    interest: state.interestAccum,
    costOfLiving: state.colAccum,
    living: state.livingAccum,
    tuition: state.tuitionAccum,
    cards: state.cardCashAccum,
  };
}

/** The current game year's ledger lines (since the last year boundary). */
export function thisYearLedger(session: Session): LedgerLines {
  const st = session.state;
  const y = session.yearStarts[session.yearStarts.length - 1];
  if (!y) return { earned: 0, interest: 0, costOfLiving: 0, living: 0, tuition: 0, cards: 0 };
  return {
    earned: st.earnedAccum - y.earnedAccum,
    interest: st.interestAccum - y.interestAccum,
    costOfLiving: st.colAccum - y.colAccum,
    living: st.livingAccum - y.livingAccum,
    tuition: st.tuitionAccum - y.tuitionAccum,
    cards: st.cardCashAccum - y.cardCashAccum,
  };
}

/**
 * The §6 accounting identity, evaluated from the same accumulators the ledger
 * displays: S0 + earned + interest − col − Living − tuition + cards. Returns
 * the reproduced balance and its difference from the true balance (must be
 * < $0.01 — surfaced in the UI as the UX-6 check, asserted in tests).
 */
export function ledgerIdentity(state: State, P: Params): { reproduced: number; diff: number } {
  const reproduced =
    P.S0 +
    state.earnedAccum +
    state.interestAccum -
    state.colAccum -
    state.livingAccum -
    state.tuitionAccum +
    state.cardCashAccum;
  return { reproduced, diff: Math.abs(reproduced - state.S) };
}

/**
 * Income rate ($/yr) under the currently applied policy. Delegates to the
 * engine — never a second copy of engine math in the app (M1 "same code"
 * rule): what the screen shows IS what the ledger accrues, by construction.
 */
export function incomeRate(state: State, P: Params): number {
  return currentIncome(state, P);
}

/** The latest statement: cash + morale deltas over the most recent substep. */
export function latestStatement(
  session: Session,
): { deltaS: number; deltaM: number; age: number } | null {
  const n = session.samples.length;
  if (n < 2) return null;
  const a = session.samples[n - 2]!;
  const b = session.samples[n - 1]!;
  return { deltaS: b.S - a.S, deltaM: b.M - a.M, age: b.age };
}

// ---------------------------------------------------------------------------
// Morale read-models (M4). All numbers come from engine exports — moraleTargetFor,
// livingGain, capacityMult, crossTime — never re-derived here (M1 "same code"
// rule): the ghost on the dial IS the fixed point the substeps integrate toward.
// ---------------------------------------------------------------------------

export interface MoraleModel {
  M: number;
  /** false once the life has ended — morale is frozen, nothing drifts */
  alive: boolean;
  /** the equilibrium of the plan in effect — the ghost on the dial */
  Mstar: number;
  /** income capacity from morale: 1.0 → burnoutMult across the burnout band */
  capacity: number;
  /** drain/gain decomposition of M*: D = mode + study; G = Living purchase */
  modeDrain: number;
  studyDrain: number;
  gain: number;
  /** game-years until M is within 5 pts of M* (null when already there, or dead) */
  settleYears: number | null;
}

export function moraleModel(session: Session, P: Params): MoraleModel {
  const st = session.state;
  // the engine's own policy normalization (M1 same-code rule: the drain/gain
  // decomposition must normalize exactly as doSubstep integrates — broke lock
  // zeroes the spend term, unknown modes fall back to 'std')
  const { mode, frac } = normalizePolicy(st, P);
  const Mstar = moraleTargetFor(st, P);
  // time until |M − M*| < 5: the §15.1-4 arrival formula, via the engine's
  // guarded crossTime (null when unreachable/degenerate/already inside).
  // Dead lives freeze morale at its final value: no drift, no countdown.
  const target = Mstar + (st.M > Mstar ? 5 : -5);
  const settleYears =
    !st.alive || Math.abs(st.M - Mstar) <= 5 ? null : crossTime(st.M, Mstar, P.kM, target);
  return {
    M: st.M,
    alive: st.alive,
    Mstar,
    capacity: capacityMult(st.M, P),
    modeDrain: P.drain[mode],
    studyDrain: st.studying ? P.studyDrain : 0,
    gain: livingGain(frac, P.wages[st.J]!, P),
    settleYears,
  };
}

/** One rung of the §15.1-4 warning ladder. */
export interface LadderRung {
  threshold: number;
  label: string;
  /** 'below' = already past it; 'approaching' = countdown running; 'clear' */
  status: 'below' | 'approaching' | 'clear';
  /** game-years to arrival at current drift (approaching only) */
  yearsToArrival: number | null;
}

// ---------------------------------------------------------------------------
// Skill read-model (M5). Gates and speeds come from engine exports (canEnroll,
// studySpeed, moraleTargetFor, crossTime) — never re-derived here.
// ---------------------------------------------------------------------------

export interface SkillModel {
  /** highest completed skill / current job tier */
  K: number;
  J: number;
  /** false once the life has ended — progress and gates are frozen */
  alive: boolean;
  /** the next skill on the ladder, or null at the top */
  next: number | null;
  studying: boolean;
  /** tuition already paid for `enrolledSkill` (resume without re-paying) */
  enrolledSkill: number;
  /** partial progress in study-years, and the requirement for `next` */
  progress: number;
  required: number | null;
  /** current study speed as a fraction of full (g(M), frozen per substep) */
  speed: number;
  /** tuition for `next` ($) */
  tuition: number | null;
  /** engine verdict: can the review enrol in `next` right now? */
  canEnrollNow: boolean;
  /** why not (every failed gate, UI-ready) */
  gates: {
    /**
     * The Skill-4 morale wall (§15.1-4b) — present only when it applies as an
     * enrolment-day precondition still ahead: NOT while already studying the
     * gated skill, and NOT while paused with paid progress (resume needs no
     * re-check until Degree Rusting lapses the enrolment).
     */
    m4?: {
      required: number;
      current: number;
      /** the settle point M* of the plan in effect — the number the blunt
       * truth quotes ("M settles at X"), NOT the live M */
      mStar: number;
      ready: boolean;
      /** years until M reaches the gate at current drift (null = unreachable
       * on this plan, or dead — countdowns freeze with the life) */
      countdown: number | null;
      /** the current plan's M* never reaches the gate (M* <= gate: an
       * asymptotic approach never crosses — classification is exhaustive) */
      neverOnThisPlan: boolean;
    };
    /** the Skill 5/6 sabbatical-time gate, present only when it applies */
    sab?: { requiredYears: number; haveYears: number; ready: boolean };
    /** Living (incl. tuition) is broke-locked */
    broke: boolean;
  };
  /** paused progress is decaying (Degree Rusting) */
  decayWarning: boolean;
}

export function skillModel(session: Session, P: Params): SkillModel {
  const st = session.state;
  const next = st.K < 6 ? st.K + 1 : null;
  const Mstar = moraleTargetFor(st, P);
  const gates: SkillModel['gates'] = { broke: st.brokeLock };
  // the M gate is an enrolment-day precondition: once enrolled (studying, or
  // paused with paid progress, st.enrolledSkill === next && p > 0) it no
  // longer applies — resume never re-checks it (engine.applyPlan), so showing
  // a "wall" then would contradict PlanPanel's own resume offer
  const gatePassed = st.studying || (next !== null && st.enrolledSkill === next && st.p > 0);
  if (next !== null && P.enrollMSkills.includes(next) && !gatePassed) {
    const ready = st.M >= P.enrollM;
    // M* <= gate is "never on this plan": the exponential approach to M*
    // never crosses it (exhaustive with ready/countdown — no unexplained
    // blocked state on the one instrument that must always explain itself)
    const never = !ready && Mstar <= P.enrollM;
    gates.m4 = {
      required: P.enrollM,
      current: st.M,
      mStar: Mstar,
      ready,
      countdown:
        ready || never || !st.alive ? null : crossTime(st.M, Mstar, P.kM, P.enrollM),
      neverOnThisPlan: never,
    };
  }
  if (next !== null && P.sabRequiredForSkills.includes(next)) {
    gates.sab = {
      requiredYears: P.sabMinYears,
      haveYears: st.sabYearsSinceSkill,
      ready: st.sabYearsSinceSkill >= P.sabMinYears - 1e-9,
    };
  }
  return {
    K: st.K,
    J: st.J,
    alive: st.alive,
    next,
    studying: st.studying,
    enrolledSkill: st.enrolledSkill,
    progress: st.p,
    required: next !== null ? P.studyYears[next]! : null,
    speed: studySpeed(st.M, P),
    tuition: next !== null ? P.tuition[next]! : null,
    canEnrollNow: next !== null && canEnroll(st, next, P),
    gates,
    decayWarning: !st.studying && st.p > 0,
  };
}

// ---------------------------------------------------------------------------
// Trajectory view (GDD §15.1-3): the curve so far + the projection under the
// current plan + reference ghosts. All engine-computed.
// ---------------------------------------------------------------------------

export interface TrajectoryPoint {
  age: number;
  S: number;
}

export interface Trajectory {
  /** net worth so far (thinned samples) */
  past: TrajectoryPoint[];
  /** projected forward under the current plan (continuation semantics) */
  projected: TrajectoryPoint[];
  /** projected death age under the current plan (null = maxAge guard hit) */
  projectedDeath: number | null;
  /** current doubling time in years (null when not meaningfully growing) */
  doublingYears: number | null;
}

/** True when the session sits exactly at a year boundary whose review has not
 * been filed — the state driveTo's auto-file (and submitPlan's review
 * classification) keys on. */
function atUnreviewedBoundary(st: State, session: Session, P: Params): boolean {
  const perYear = Math.round(1 / P.substep);
  return st.n % perYear === 0 && session.reviewedYears === st.n / perYear;
}

/**
 * THE continuation projector — the one function every forward-looking
 * instrument (trajectory, projectDeath, healthModel's ladder) settles its
 * clone through, so all three project the SAME future (the M1 same-code rule
 * applied to the driver itself). Semantics mirror driveTo's auto-file
 * bit-for-bit:
 * - the carried plan's study is the STANDING logged intent, not the transient
 *   st.studying — inside a completion→boundary window the auto-file re-enrols
 *   and the projection must show that climb, not a flatline;
 * - `reviewPending` (the session sits at an unreviewed boundary) applies the
 *   continuation review BEFORE the first projected year (promotion + enrolment
 *   effects included);
 * - after every applied review the carried study intent re-baselines to the
 *   enacted state (the same enacted-study rewrite applyEntry performs on the
 *   log), so a projected refusal stops retrying exactly as the live log would.
 * Hooks: onBoundary fires at each settled year boundary (or at death);
 * onSubstep fires after every substep (G7: substep-wise driving is
 * bit-identical to settleTo). Mutates `st` and `plan` in place.
 */
function settleContinuation(
  st: State,
  plan: PlanAction,
  reviewPending: boolean,
  P: Params,
  hooks?: { onBoundary?: (st: State) => void; onSubstep?: (st: State) => void },
): void {
  const perYear = Math.round(1 / P.substep);
  if (st.alive && reviewPending) {
    applyPlan(st, { ...plan }, P);
    plan.study = st.studying;
  }
  let guard = 0;
  while (st.alive && ageOfState(st, P) < P.maxAge && guard++ < 110) {
    // next boundary: finish the current partial year, then whole years
    const nextBoundaryN = (Math.floor(st.n / perYear) + 1) * perYear;
    if (hooks?.onSubstep) {
      while (st.alive && st.n < nextBoundaryN) {
        doSubstep(st, P);
        hooks.onSubstep(st);
      }
    } else {
      settleTo(st, P.startAge + nextBoundaryN * P.substep, P);
    }
    hooks?.onBoundary?.(st);
    if (!st.alive) break;
    applyPlan(st, { ...plan }, P);
    plan.study = st.studying;
  }
}

/**
 * Project the life forward under "last year's policy continues" (GDD §3.2):
 * the continuation plan persists at every future review — exactly what
 * driveTo's auto-file would do if the player never touched anything again
 * (parity asserted against a driveTo clone in apps/web/test/skills.test.ts).
 * Engine-exact via a cloned state; yearly resolution.
 */
export function trajectory(session: Session, P: Params): Trajectory {
  const memo = memoFor(session);
  if (memo.has('traj')) return memo.get('traj') as Trajectory;
  const past: TrajectoryPoint[] = session.samples.map((s) => ({ age: s.age, S: s.S }));
  const st = structuredClone(session.state);
  const projected: TrajectoryPoint[] = [];
  const plan: PlanAction = {
    mode: st.policy.mode,
    livingFrac: st.policy.livingFrac,
    study: continuationStudy(session),
  };
  settleContinuation(st, plan, st.alive && atUnreviewedBoundary(st, session, P), P, {
    onBoundary: (s) =>
      projected.push({
        age: s.alive ? P.startAge + s.n * P.substep : s.deathAge!,
        S: s.S,
      }),
  });
  // doubling time from the trailing 3 game-years of growth (needs positive,
  // growing balance to mean anything)
  let doublingYears: number | null = null;
  const n = session.samples.length;
  const lookback = 36; // substeps
  if (n > lookback) {
    const a = session.samples[n - 1 - lookback]!;
    const b = session.samples[n - 1]!;
    if (a.S > 0 && b.S > a.S) {
      const rate = Math.log(b.S / a.S) / (b.age - a.age);
      if (rate > 1e-6) doublingYears = Math.LN2 / rate;
    }
  }
  const out: Trajectory = {
    past,
    projected,
    projectedDeath: st.alive ? null : st.deathAge!,
    doublingYears,
  };
  memo.set('traj', out);
  return out;
}

function ageOfState(st: State, P: Params): number {
  return st.alive ? P.startAge + st.n * P.substep : st.deathAge!;
}

/**
 * The morale warning ladder (GDD §15.1-4, v1.2.0 copy): every downward
 * threshold shows time-to-arrival at the current plan's drift, from the same
 * closed form the engine integrates. Guard: a countdown renders only when the
 * threshold lies strictly between M and M* (crossTime returns null otherwise)
 * — never a negative or NaN, and "announced before crossing, never after".
 * Countdowns are "at CURRENT drift": a state-driven M* change (broke-lock
 * engagement, skill completion once M5 unpins study) re-baselines every rung
 * on the next render. Dead lives freeze morale, so no rung ever counts down
 * on the death screen — 'below'/'clear' report the frozen M truthfully.
 */
export function warningLadder(session: Session, P: Params): LadderRung[] {
  const st = session.state;
  const Mstar = moraleTargetFor(st, P);
  const rungs = [
    { threshold: 55, label: 'Tired' },
    { threshold: 40, label: 'Running on fumes' },
    { threshold: P.burnoutHi, label: 'Burning out — capacity falling' },
    { threshold: P.burnoutLo, label: 'Burnt out — capacity floor' },
  ];
  return rungs.map(({ threshold, label }) => {
    // the burnoutHi rung announces "capacity falling", which is strict
    // M < burnoutHi (capacityMult(burnoutHi) = 1.0 exactly — at the band top
    // the capacity line still reads 100%); the other rungs are at-or-below
    const below = threshold === P.burnoutHi ? st.M < threshold : st.M <= threshold;
    if (below) {
      return { threshold, label, status: 'below' as const, yearsToArrival: null };
    }
    const t = st.alive ? crossTime(st.M, Mstar, P.kM, threshold) : null;
    return t !== null && t > 0
      ? { threshold, label, status: 'approaching' as const, yearsToArrival: t }
      : { threshold, label, status: 'clear' as const, yearsToArrival: null };
  });
}

// ---------------------------------------------------------------------------
// M6 read-models: the projection centerpiece, health, and the death screen.
// Engine-exact throughout — clones settled through the real substeps.
// ---------------------------------------------------------------------------

// The projection family (projectDeath / healthModel / trajectory) each settle
// a cloned life to death — thousands of substeps. They are pure functions of
// the settled state + plan, so they are memoized per session, invalidated the
// moment anything that can move them changes (a substep, a filed entry, a
// policy or study change, death). Without this, every React render re-ran
// several full-life simulations (measured ~seconds per Step click in dev).
type MemoCarrier = Session & { _memo?: { key: string; map: Map<string, unknown> } };

// INVARIANT: every input a memoized projection reads (state fields, plus
// lastPlan(save).study via continuationStudy) must change ONLY alongside a
// key field. Today that holds: every log mutation (review push, adjustment
// echoing the standing intent, auto-file, the enacted-study rewrite) bumps
// logIndex; substeps bump n; death flips alive. M7 OBLIGATION: any card
// resolution that mutates H/M/S must go through a logged entry (bumping
// logIndex) or be added to this key — an unlogged applyCard would serve a
// stale cached projection at the exact moment the card is answered.
function memoFor(session: Session): Map<string, unknown> {
  const st = session.state;
  const key =
    `${st.n}|${st.alive}|${st.policy.mode}|${st.policy.livingFrac}|` +
    `${st.studying}|${st.brokeLock}|${st.K}|${st.enrolledSkill}|` +
    `${session.reviewedYears}|${session.logIndex}`;
  const c = session as MemoCarrier;
  if (!c._memo || c._memo.key !== key) c._memo = { key, map: new Map() };
  return c._memo.map;
}

/**
 * Projected death age under "the current plan held constant" (GDD §8) — or,
 * with a draft, under that draft applied exactly the way submitPlan would
 * classify it right now. Deterministic, therefore exact: this is the number
 * that must move the moment a control is touched. Returns null only if the
 * maxAge guard is reached (unreachable under the erosion curve).
 */
export function projectDeath(
  session: Session,
  P: Params,
  draft?: PlanDraft,
): number | null {
  const memo = memoFor(session);
  const mkey = draft
    ? `proj|${draft.mode}|${draft.livingFrac}|${!!draft.study}`
    : 'proj';
  if (memo.has(mkey)) return memo.get(mkey) as number | null;
  const st = structuredClone(session.state);
  if (!st.alive) return st.deathAge;
  // The continuation the clone lives is settleContinuation's — the same one
  // trajectory and healthModel project, and the same one driveTo enacts.
  let plan: PlanAction;
  let reviewPending = false;
  if (draft) {
    if (atUnreviewedBoundary(st, session, P)) {
      // the draft IS this year's review; the carried plan holds the ENACTED
      // study state (a refused enrolment must not retry — applyEntry's rule)
      applyPlan(st, { mode: draft.mode, livingFrac: draft.livingFrac, study: !!draft.study }, P);
      plan = { mode: st.policy.mode, livingFrac: st.policy.livingFrac, study: st.studying };
    } else {
      // a mid-year adjustment never touches study: the logged entry echoes the
      // STANDING intent (submitPlan), so the continuation carries it forward
      applyAdjustment(st, { mode: draft.mode, livingFrac: draft.livingFrac });
      plan = {
        mode: st.policy.mode,
        livingFrac: st.policy.livingFrac,
        study: continuationStudy(session),
      };
    }
  } else {
    plan = {
      mode: st.policy.mode,
      livingFrac: st.policy.livingFrac,
      study: continuationStudy(session),
    };
    reviewPending = atUnreviewedBoundary(st, session, P);
  }
  settleContinuation(st, plan, reviewPending, P);
  const out = st.alive ? null : st.deathAge;
  memo.set(mkey, out);
  return out;
}

export interface HealthModel {
  H: number;
  alive: boolean;
  /** itemised per-year rates at the current frozen flags (engine-owned) */
  rates: ReturnType<typeof healthRates>;
  frail: boolean;
  /** projected death age under the held plan (the centerpiece number) */
  projected: number | null;
  /** in final-years mode: Projected − age ≤ 3 (GDD §8 / §15.1-6) */
  finalYears: boolean;
  /** health warning ladder (§15.1-4): years until H crosses each threshold */
  ladder: { threshold: number; label: string; status: 'below' | 'approaching' | 'clear'; yearsToArrival: number | null }[];
}

export function healthModel(session: Session, P: Params): HealthModel {
  const memo = memoFor(session);
  if (memo.has('health')) return memo.get('health') as HealthModel;
  const st = session.state;
  const projected = projectDeath(session, P);
  const age = st.alive ? P.startAge + st.n * P.substep : st.deathAge!;
  const rungs: HealthModel['ladder'] = [];
  const thresholds = [
    { threshold: 50, label: 'Aching' },
    { threshold: P.frailH, label: 'Failing — capped at 60% capacity' },
  ];
  // measure crossings by settling a clone through settleContinuation — the
  // SAME future projectDeath and trajectory project (health has no closed
  // form, so the countdown IS a measurement of that one shared future)
  const crossings: (number | null)[] = thresholds.map(() => null);
  if (st.alive) {
    const clone = structuredClone(st);
    const plan: PlanAction = {
      mode: clone.policy.mode,
      livingFrac: clone.policy.livingFrac,
      study: continuationStudy(session),
    };
    let prevH = clone.H;
    settleContinuation(clone, plan, atUnreviewedBoundary(clone, session, P), P, {
      onSubstep: (c) => {
        const nowAge = c.alive ? P.startAge + c.n * P.substep : c.deathAge!;
        thresholds.forEach((t, i) => {
          if (crossings[i] === null && prevH > t.threshold && c.H <= t.threshold) {
            crossings[i] = nowAge - age;
          }
        });
        prevH = c.H;
      },
    });
  }
  thresholds.forEach((t, i) => {
    const crossing = crossings[i] ?? null;
    if (!st.alive || st.H <= t.threshold) {
      rungs.push({ ...t, status: st.H <= t.threshold ? 'below' : 'clear', yearsToArrival: null });
    } else if (crossing !== null) {
      rungs.push({ ...t, status: 'approaching', yearsToArrival: crossing });
    } else {
      rungs.push({ ...t, status: 'clear', yearsToArrival: null });
    }
  });
  const out: HealthModel = {
    H: st.H,
    alive: st.alive,
    rates: healthRates(st, P),
    frail: st.H < P.frailH,
    projected,
    finalYears: st.alive && projected !== null && projected - age <= 3,
    ladder: rungs,
  };
  memo.set('health', out);
  return out;
}

/**
 * Which §9.3 sentence the counterfactual panel closes with. Classified here
 * (not in JSX) so the copy PG-3 is graded on is pinned by tests:
 * - 'investment': the spending carried the estate — banking collapses it to
 *   under half (the balanced thesis: "the spending was the investment");
 * - 'respect': banking still finishes behind, but most of the estate survives
 *   it — the grinder's minimal spending was correct WITHIN his strategy
 *   (§9.3's "Banking even your $3.4k/yr" framing, never the thesis line);
 * - 'time': no Living was ever spent — the whole delta is banked TIME (the
 *   sab→std transform), not dollars;
 * - 'banked': nothing was spent and nothing differs — this life WAS the
 *   counterfactual;
 * - 'ahead': banking would have finished ahead on the scoreboard.
 */
export type CfVerdict = 'investment' | 'respect' | 'time' | 'banked' | 'ahead';

export interface DeathModel {
  estate: number;
  deathAge: number;
  skillAges: number[];
  K: number;
  /** the Life Ledger (§9.2) */
  ledger: {
    lines: LedgerLines;
    /** share of a POSITIVE estate that was compound interest; 0 when the
     * estate is non-positive (98% "of the debt" is not G12's number) */
    interestShare: number;
    flourishYears: number;
    minCash: number;
    earnedTotal: number;
    livedOnTotal: number;
    /** §9.2 "sabbaticals … bought": game-years spent in sab mode, and the
     * count of distinct stretches — integrated from the log (the truth) */
    sabYears: number;
    sabStretches: number;
  };
  /** the banked-everything re-sim (§9.3) */
  counterfactual: { estate: number; deathAge: number; K: number };
  verdict: CfVerdict;
  /** the v2 inheritance object, ready to download */
  estateRecord: ReturnType<typeof estateRecordOf>;
}

/** The death screen's read-model. Only meaningful once the life has ended.
 * Memoized (a dead state's key is stable): the counterfactual is a full-life
 * replay — the exact cost class the memo exists for. */
export function deathModel(session: Session, P: Params): DeathModel {
  const memo = memoFor(session);
  if (memo.has('death')) return memo.get('death') as DeathModel;
  const st = session.state;
  const cf = counterfactual(session.save, P);
  const lines = lifetimeLedger(st);
  const deathAge = st.deathAge ?? P.startAge + st.n * P.substep;
  // Sabbatical time, integrated over the log: each entry's mode holds from its
  // atAge to the next entry (or death). The log carries every policy change —
  // reviews, adjustments, AND driveTo's auto-files — so this is exact.
  let sabYears = 0;
  let sabStretches = 0;
  let prevSab = false;
  const log = session.save.log;
  for (let i = 0; i < log.length; i++) {
    const e = log[i]!;
    const isSab = e.plan.mode === 'sab';
    if (isSab) {
      const until = i + 1 < log.length ? log[i + 1]!.atAge : deathAge;
      sabYears += Math.max(0, Math.min(until, deathAge) - e.atAge);
      if (!prevSab) sabStretches += 1;
    }
    prevSab = isSab;
  }
  const spentTotal = st.livingAccum + st.tuitionAccum;
  const cfEstateDelta = cf.estate - st.S;
  let verdict: CfVerdict;
  if (spentTotal <= 1 && Math.abs(cfEstateDelta) <= 1) verdict = 'banked';
  else if (spentTotal <= 1) verdict = 'time';
  else if (cfEstateDelta < -1 && st.S > 0 && cf.estate < 0.5 * st.S) verdict = 'investment';
  else if (cfEstateDelta < -1) verdict = 'respect';
  else if (Math.abs(cfEstateDelta) <= 1) verdict = 'banked';
  else verdict = 'ahead';
  const out: DeathModel = {
    estate: st.S,
    deathAge,
    skillAges: [...st.skillAges],
    K: st.K,
    ledger: {
      lines,
      interestShare: st.S > 0 ? st.interestAccum / st.S : 0,
      flourishYears: st.flourishYears,
      minCash: st.minS,
      earnedTotal: st.earnedAccum,
      livedOnTotal: st.colAccum + st.livingAccum,
      sabYears,
      sabStretches,
    },
    counterfactual: { estate: cf.estate, deathAge: cf.deathAge, K: cf.K },
    verdict,
    estateRecord: estateRecordOf(st, session.save, P),
  };
  memo.set('death', out);
  return out;
}
